import { Challenge, ChallengeAttributes, ChallengeLearningOutcome, Clue } from '@/src/types/Challenge';
import { TranslatedAttribute } from '@/src/types/common';
import { cloneDeep, set, sortBy } from 'lodash';

export const dividerStart = (attribute: string) => {
  return `<!-- ${attribute} -->` + '\n';
};

export const dividerEnd = (attribute: string) => {
  return '\n' + `<!-- /${attribute} -->` + '\n';
};

export const formattedData = (sectionName: string, sectionData: string | undefined | null) => {
  return `${dividerStart(sectionName)}${sectionData || ''}${dividerEnd(sectionName)}`;
};

const getAttributeContent = (content: string, attribute: string) => {
  const start = dividerStart(attribute);
  const end = dividerEnd(attribute);
  if (!content.includes(start) || !content.includes(end)) {
    return '';
  }

  // example of a string being manipulated:
  // content = "start - the text we want - end - some random text"
  // start = "start"
  // end = "end"
  return (
    content
      .split(start) // -> ["", "start", " - the text we want - end - some random text"]
      .pop() || ''
  ) // -> " - the text we want - end - some random text"
    .split(end)[0]; // returns first element of [" - the text we want - ", "end", " - some random text"]
};

export const indexedDivider = (attribute: string, index: number) => {
  return `${attribute}${index}`;
};

export const exportChallengeForTranslation = (challenge?: Challenge | null) => {
  let content = '';
  if (!challenge?.props) {
    return content;
  }
  // first, append the version that we are translating
  content += dividerStart(`VERSION: ${challenge.majorVersion || 0}.${challenge.minorVersion || 0}`);
  content += formattedData(ChallengeAttributes.TITLE, challenge.props.title);

  content += formattedData(ChallengeAttributes.CATEGORY, challenge.props.category);
  content += formattedData(ChallengeAttributes.DESCRIPTION, challenge.props.description);

  const challengeTasks = ((tasks) => {
    if (!tasks) return [];
    return sortBy(tasks, 'taskNumber');
  })(challenge?.props?.tasks);

  challengeTasks.forEach((task, taskIndex) => {
    let taskContent = '';
    taskContent += formattedData(ChallengeAttributes.TASK_TITLE, task.title);
    taskContent += formattedData(ChallengeAttributes.TASK_CONTENT, task.content);
    task.clues.forEach((clue, clueIndex) => {
      let clueContent = '';
      clueContent += formattedData(indexedDivider(ChallengeAttributes.CLUE_TITLE, clueIndex), clue.title);
      clueContent += formattedData(indexedDivider(ChallengeAttributes.CLUE_DESCRIPTION, clueIndex), clue.description);
      taskContent += formattedData(indexedDivider(ChallengeAttributes.CLUE, clueIndex), clueContent);
    });
    content += formattedData(indexedDivider(ChallengeAttributes.TASK, taskIndex), taskContent);
  });

  if (challenge.props.learningOutcome) {
    let learningOutcome = '';
    learningOutcome += formattedData(
      ChallengeAttributes.LEARNING_OUTCOME_SUMMARY,
      challenge.props.learningOutcome.summary
    );
    learningOutcome += formattedData(
      ChallengeAttributes.LEARNING_OUTCOME_CONTENT,
      challenge.props.learningOutcome.content
    );

    content += formattedData(ChallengeAttributes.LEARNING_OUTCOME, learningOutcome);
  }

  return content;
};

export const parseTranslationFile = (translatedChallenge: Challenge, content: string): Challenge => {
  content = content.replace(/\r\n/g, '\n');
  const modified = cloneDeep(translatedChallenge);
  const challengeTasks = ((tasks) => {
    if (!tasks) return [];
    return sortBy(tasks, 'taskNumber');
  })(translatedChallenge?.props?.tasks);

  modified.props[ChallengeAttributes.TITLE] = getAttributeContent(content, ChallengeAttributes.TITLE);
  modified.props[ChallengeAttributes.CATEGORY] = getAttributeContent(content, ChallengeAttributes.CATEGORY);
  modified.props[ChallengeAttributes.DESCRIPTION] = getAttributeContent(content, ChallengeAttributes.DESCRIPTION);

  let taskIndex = 0;
  const newTasks = [];
  let rawTask = '';
  // get only the raw task string so that there's less content to search through
  while ((rawTask = getAttributeContent(content, indexedDivider(ChallengeAttributes.TASK, taskIndex)))) {
    // clone this task so that we can retain its metadata but modify its contents
    if (translatedChallenge.props.tasks.length - 1 < taskIndex) {
      throw Error('Task to translate not found');
    }
    const task = Object.assign(cloneDeep(challengeTasks[taskIndex]), {
      title: getAttributeContent(rawTask, `${ChallengeAttributes.TASK_TITLE}`),
      content: getAttributeContent(rawTask, `${ChallengeAttributes.TASK_CONTENT}`),
    });

    let clueIndex = 0;
    const newClues = [];
    let rawClue = '';
    while ((rawClue = getAttributeContent(rawTask, indexedDivider(ChallengeAttributes.CLUE, clueIndex)))) {
      if (task.clues.length - 1 < clueIndex) {
        throw Error('Task Clue to translate not found');
      }
      const clue = cloneClue(task.clues[clueIndex]);
      if (!clue) {
        throw Error('Invalid clue index');
      }
      clue.title = getAttributeContent(rawClue, indexedDivider(ChallengeAttributes.CLUE_TITLE, clueIndex));
      clue.description = getAttributeContent(rawClue, indexedDivider(ChallengeAttributes.CLUE_DESCRIPTION, clueIndex));
      newClues.push(clue);
      clueIndex++;
    }

    // completely replace the original list of clues with our new ones
    if (newClues.length > 0) {
      task.clues = newClues;
    }
    newTasks.push(task);
    taskIndex++;
  }
  // completely replace the original list of tasks with our new ones
  if (newTasks.length > 0) {
    modified.props.tasks = newTasks;
  }

  if (modified.props.learningOutcome) {
    modified.props.learningOutcome = Object.assign(new ChallengeLearningOutcome(), {
      summary: getAttributeContent(content, ChallengeAttributes.LEARNING_OUTCOME_SUMMARY),
      content: getAttributeContent(content, ChallengeAttributes.LEARNING_OUTCOME_CONTENT),
    });
  }

  return modified;
};

const toTranslatedAttribute = (obj: { attributeName: string; originalText: string | null; translatedText: string }) =>
  Object.assign(new TranslatedAttribute(), obj);

export const parseTranslationFileToFormData = (
  translatedChallenge: Challenge,
  content: string
): TranslatedAttribute[] => {
  content = content.replace(/\r\n/g, '\n');
  const translatedAttributes: TranslatedAttribute[] = [];

  translatedAttributes.push(
    toTranslatedAttribute({
      attributeName: ChallengeAttributes.TITLE,
      originalText: translatedChallenge.props[ChallengeAttributes.TITLE],
      translatedText: getAttributeContent(content, ChallengeAttributes.TITLE),
    })
  );
  translatedAttributes.push(
    toTranslatedAttribute({
      attributeName: ChallengeAttributes.CATEGORY,
      originalText: translatedChallenge.props[ChallengeAttributes.CATEGORY],
      translatedText: getAttributeContent(content, ChallengeAttributes.CATEGORY),
    })
  );
  translatedAttributes.push(
    toTranslatedAttribute({
      attributeName: ChallengeAttributes.DESCRIPTION,
      originalText: translatedChallenge.props[ChallengeAttributes.DESCRIPTION],
      translatedText: getAttributeContent(content, ChallengeAttributes.DESCRIPTION),
    })
  );

  let taskIndex = 0;
  let rawTask = '';
  // get only the raw task string so that there's less content to search through
  while ((rawTask = getAttributeContent(content, indexedDivider(ChallengeAttributes.TASK, taskIndex)))) {
    const currTask = translatedChallenge.props.tasks.find((t) => t.taskNumber === taskIndex + 1);
    if (!currTask) {
      throw Error('Task to translate not found');
    }
    const taskId = currTask?.id;
    translatedAttributes.push(
      toTranslatedAttribute({
        attributeName: `task.${taskId}.title`,
        originalText: currTask?.title || '',
        translatedText: getAttributeContent(rawTask, `${ChallengeAttributes.TASK_TITLE}`),
      })
    );
    translatedAttributes.push(
      toTranslatedAttribute({
        attributeName: `task.${taskId}.content`,
        originalText: currTask?.content || '',
        translatedText: getAttributeContent(rawTask, `${ChallengeAttributes.TASK_CONTENT}`),
      })
    );

    let clueIndex = 0;
    let rawClue = '';
    const cluesSorted = sortBy(currTask.clues, 'order');
    while ((rawClue = getAttributeContent(rawTask, indexedDivider(ChallengeAttributes.CLUE, clueIndex)))) {
      const currClue = cluesSorted[clueIndex];
      if (!currClue) {
        throw Error('Task Clue to translate not found');
      }
      translatedAttributes.push(
        toTranslatedAttribute({
          attributeName: `task.${taskId}.clue.${clueIndex}.title`,
          originalText: currClue.title || '',
          translatedText: getAttributeContent(rawClue, indexedDivider(ChallengeAttributes.CLUE_TITLE, clueIndex)),
        })
      );

      translatedAttributes.push(
        toTranslatedAttribute({
          attributeName: `task.${taskId}.clue.${clueIndex}.description`,
          originalText: currClue.description || '',
          translatedText: getAttributeContent(rawClue, indexedDivider(ChallengeAttributes.CLUE_DESCRIPTION, clueIndex)),
        })
      );
      clueIndex++;
    }
    taskIndex++;
  }

  if (translatedChallenge.props.learningOutcome) {
    const learningOutcomeContent = getAttributeContent(content, ChallengeAttributes.LEARNING_OUTCOME_CONTENT);
    if (learningOutcomeContent) {
      translatedAttributes.push(
        toTranslatedAttribute({
          attributeName: 'learningOutcomeContent',
          originalText: translatedChallenge.props.learningOutcome.content || '',
          translatedText: learningOutcomeContent,
        })
      );
    }

    const learningOutcomeSummary = getAttributeContent(content, ChallengeAttributes.LEARNING_OUTCOME_SUMMARY);
    if (learningOutcomeContent) {
      translatedAttributes.push(
        toTranslatedAttribute({
          attributeName: 'learningOutcomeSummary',
          originalText: translatedChallenge.props.learningOutcome.summary || '',
          translatedText: learningOutcomeSummary,
        })
      );
    }
  }

  return translatedAttributes.filter((ta) => ta.originalText !== ta.translatedText);
};

/**
 * Takes a list of translated attributes (likely from the output of the previous function)
 * and inject those values into a Challenge object
 *
 * @param translatedAttributes
 * @param challenge
 * @returns Challenge with translated attributes.
 */
export const convertTranslatedAttributesToChallenge = (
  translatedAttributes: TranslatedAttribute[],
  challenge: Challenge
): Challenge => {
  const clone = cloneDeep(challenge);
  for (const attr of translatedAttributes) {
    const task = clone?.props.tasks.find((ta) => ta.id === attr.attributeName.split('.')[1]);
    if (task && attr.attributeName.startsWith('task.')) {
      if (attr.attributeName.includes('.clue.')) {
        const clue = task.clues.find((cl) => cl.order === parseInt(attr.attributeName.split('.')[3], 10) + 1);
        if (clue) {
          set(clue, attr.attributeName.split('.')[4], attr.translatedText);
        }
      } else {
        set(task, attr.attributeName.split('.')[2], attr.translatedText);
      }
    } else {
      set(clone, `props.${attr.attributeName}`, attr.translatedText);
    }
  }
  return clone;
};

export const cloneClue = (c: Clue) => {
  if (!c) {
    return null;
  }
  return Object.assign(new Clue(), cloneDeep(c));
};
